// ════════════════════════════════════════════════════════════════════════
// Coach Review · v2 — inline icon set.
// ────────────────────────────────────────────────────────────────────────
// NEW (Coach Review v2 layout). The v2 reference uses Lucide icons,
// but Lucide is NOT a project dependency and the "no new dependencies"
// rule stands. The rest of the repo draws icons as inline `<svg>` with
// `stroke="currentColor"` (see `canvas/components/ToolGrid.tsx`); this
// module follows that exact convention so the new chrome matches.
//
// Each icon is a tiny stroked SVG sized via the `size` prop. `fill`
// defaults to none (stroked) — Play/Pause pass `fill="currentColor"`
// for the solid transport glyphs.
// ════════════════════════════════════════════════════════════════════════

type IconProps = {
  size?: number;
  /** Stroke width — defaults to 1.75 to match the canvas tool icons. */
  stroke?: number;
  /** Fill — `none` (stroked) by default. */
  fill?: string;
  className?: string;
};

function Svg({
  size = 16,
  stroke = 1.75,
  fill = "none",
  className,
  children,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function PlayIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M5 3 L19 12 L5 21 Z" />
    </Svg>
  );
}

export function PauseIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M6 4 L6 20 M18 4 L18 20" />
    </Svg>
  );
}

export function ChevronLeftIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M15 17l-5-5 5-5" />
    </Svg>
  );
}

export function ChevronRightIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M9 7l5 5-5 5" />
    </Svg>
  );
}

export function ChevronsLeftIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" />
    </Svg>
  );
}

export function ChevronsRightIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M13 7l5 5-5 5M6 7l5 5-5 5" />
    </Svg>
  );
}

export function RepeatIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M17 1l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3" />
    </Svg>
  );
}

export function FilmIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3 3h18v18H3zM3 7h2M3 11h2M3 15h2M3 19h2M19 7h2M19 11h2M19 15h2M19 19h2" />
    </Svg>
  );
}

export function PlusIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  );
}

export function SendIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
    </Svg>
  );
}

export function CloseIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M18 6 6 18M6 6l12 12" />
    </Svg>
  );
}

export function PencilIcon(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M17 3a2.828 2.828 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </Svg>
  );
}
